// Статус-смуга станції — над будь-якою вкладкою картки станції.
//
// Запит диспетчерів (зустріч 16.10) і станційної EMS «Strabichovo EMS»: першим
// поглядом видно, що станція віддає в мережу, скільки обладнання на звʼязку і
// наскільки свіжі дані. П'ять плиток: точка приєднання, СЕС, PCS, УЗЕ, звʼязок.
// «N/M» — скільки пристроїв відповідають із загальної кількості; ONLINE /
// PARTIAL / OFFLINE — той самий словник, що на станційному HMI.
//
// Невідоме не показується нулем: без даних плитка пише «—» і причину.
import i18n from '@/i18n';
import type { ReactNode } from 'react';
import { scada } from '@/scada/tokens';
import type { StationTelemetry } from '@/scada/mock/telemetry';
import type { BessFleetData } from '@/scada/useBess';
import {
  useSharedPoll, cadenceOf, linkOf, fmtAge, LINK_STYLE, fillOf, FILL_STYLE,
  kw0, signed0, n1, type Fill,
} from '@/scada/live';

interface TileProps {
  name: string;
  fill?: Fill;
  count?: string;
  value: ReactNode;
  unit?: string;
  sub: ReactNode;
  dim?: boolean;
}

function Tile({ name, fill, count, value, unit, sub, dim }: TileProps) {
  const fs = fill ? FILL_STYLE[fill] : null;
  return (
    <div style={{
      background: scada.panel, border: `1px solid ${scada.border}`, borderRadius: 12,
      boxShadow: 'var(--shadow)', padding: '10px 12px', minWidth: 0, opacity: dim ? 0.6 : 1,
    }}>
      <div className="flex items-center justify-between" style={{ gap: 6 }}>
        <span className="flex items-center" style={{ gap: 6, minWidth: 0 }}>
          {fs && <span style={{ width: 8, height: 8, borderRadius: 999, background: fs.c, flexShrink: 0 }} />}
          <span style={{ color: scada.textDim, fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        </span>
        {fs && (
          <span style={{ fontSize: 10, fontWeight: 700, color: fs.c, whiteSpace: 'nowrap' }}>
            {/* Без свіжих даних лічильник «64/64» був би старим числом — не показуємо. */}
            {fill === 'unknown' ? i18n.t('ak_scada_no_data') : `${count ? `${count} ` : ''}${fs.label}`}
          </span>
        )}
      </div>
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ color: scada.text, fontSize: 20, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        {unit && <span style={{ color: scada.textMuted, fontSize: 11 }}>{unit}</span>}
      </div>
      <div style={{ color: scada.textMuted, fontSize: 11, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
    </div>
  );
}

const FLOW_LABEL: Record<string, string> = {
  get charging() { return i18n.t('ak_scada_charging'); },
  get discharging() { return i18n.t('ak_scada_discharging'); },
  get idle() { return i18n.t('ak_scada_standby_2'); },
  get fault() { return i18n.t('ak_scada_fault'); },
};

export default function StationStatusStrip({ stationId, t }: { stationId: string; t: StationTelemetry }) {
  const cad = cadenceOf(stationId);
  const age = t.comms?.lastPacketSec ?? null;
  const link = linkOf(age, cad);
  const linkLost = link === 'lost' || link === 'unknown';

  // Розбивка УЗЕ на PCS і батареї — лише там, де накопичувач є.
  const { data: bess } = useSharedPoll<BessFleetData>(t.hasBess && t.assetId ? `/scada/bess/${t.assetId}/latest` : null, 30000);

  const bessKw = bess?.summary?.powerKw ?? t.bess?.powerKw ?? null;
  // P у точці приєднання: DIRIS кабінет не опитує (ТЗ EMS box, § 2.1 — один
  // активний клієнт), тож це ОЦІНКА: генерація СЕС + потужність УЗЕ (плюс —
  // розряд, тобто видача). Втрати трансформаторів і власні потреби не враховано.
  const pccKw = linkLost ? null : t.activePowerKw + (bessKw ?? 0);

  const invFill = linkLost ? 'unknown' : fillOf(t.invertersWorking, t.inverterCount);
  const limited = t.activeLimitKw != null;

  const pcsTotal = bess?.count ?? null;
  const pcsOnline = bess?.summary?.online ?? null;
  const batTotal = bess?.batteryCount ?? null;
  const batOnline = bess ? bess.batteries.filter((b) => b.socPct != null).length : null;
  const alarms = bess?.summary?.alarmsActive ?? 0;
  const flow = bess?.summary?.flow ?? t.bess?.mode ?? null;

  const tiles: TileProps[] = [
    {
      name: i18n.t('ak_scada_point_of_connection'),
      value: signed0(pccKw),
      unit: pccKw == null ? undefined : i18n.t('ak_scada_kw'),
      sub: pccKw == null ? i18n.t('ak_scada_no_fresh_data') : i18n.t('ak_scada_estimate', { dir: pccKw >= 0 ? i18n.t('ak_scada_export_to_grid') : i18n.t('ak_scada_import_from_grid') }),
      dim: linkLost,
    },
    {
      name: i18n.t('ak_scada_pv_plant'),
      fill: invFill,
      count: `${t.invertersWorking}/${t.inverterCount}`,
      value: linkLost ? '—' : kw0(t.activePowerKw),
      unit: linkLost ? undefined : i18n.t('ak_scada_kw'),
      sub: limited ? i18n.t('ak_scada_limit_kw', { kw: kw0(t.activeLimitKw) }) : i18n.t('ak_scada_inverters_online_2'),
      dim: linkLost,
    },
  ];

  if (t.hasBess) {
    tiles.push(
      {
        name: 'PCS',
        fill: linkLost ? 'unknown' : fillOf(pcsOnline, pcsTotal),
        count: pcsTotal ? `${pcsOnline ?? 0}/${pcsTotal}` : undefined,
        value: linkLost ? '—' : signed0(bessKw),
        unit: linkLost || bessKw == null ? undefined : i18n.t('ak_scada_kw'),
        sub: alarms > 0 ? i18n.t('ak_scada_active_alarms', { n: alarms }) : i18n.t('ak_scada_discharge_charge'),
        dim: linkLost,
      },
      {
        name: i18n.t('ak_scada_bess'),
        fill: linkLost ? 'unknown' : fillOf(batOnline, batTotal),
        count: batTotal ? `${batOnline ?? 0}/${batTotal}` : undefined,
        value: linkLost ? '—' : n1(bess?.summary?.socPct ?? t.bess?.socPct ?? null),
        unit: linkLost ? undefined : '% SOC',
        sub: flow ? i18n.t('ak_scada_mode', { m: FLOW_LABEL[flow] ?? flow }) : i18n.t('ak_scada_batteries_online'),
        dim: linkLost,
      },
    );
  }

  tiles.push({
    name: i18n.t('ak_scada_link'),
    value: <span style={{ color: LINK_STYLE[link].c }}>{LINK_STYLE[link].label}</span>,
    sub: `${age == null ? i18n.t('ak_scada_no_data') : i18n.t('ak_scada_updated', { a: fmtAge(age) })} · ${cad.label}`,
  });

  return (
    <div
      className={`grid grid-cols-2 gap-2.5 ${tiles.length === 5 ? 'md:grid-cols-5' : 'md:grid-cols-3'}`}
      style={{ marginBottom: 16 }}
      aria-label={i18n.t('ak_scada_station_status')}
    >
      {tiles.map((tile) => <Tile key={tile.name} {...tile} />)}
    </div>
  );
}
